import React from 'react';
import { useDragLayer } from 'react-dnd';
import { SHAPE_CONFIGS } from './TableShape';
import { VENUE_ELEMENT_CONFIGS } from './VenueElementShape';
import { CUSTOM_PIECE_CONFIGS } from './CustomPieceShape';

// Render exact table shape SVG for drag preview - matches canvas rendering exactly
// WITHOUT seats, just the shape outline
function renderDragPreviewSVG(shape, width, height) {
  const strokeColor = '#D4AF37';
  const fillColor = 'rgba(51, 65, 85, 0.85)';
  const svgStyle = { position: 'absolute', inset: 0 };
  
  switch (shape) {
    case 'round':
    case 'circle':
      return (
        <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} style={svgStyle}>
          <ellipse 
            cx={width / 2} 
            cy={height / 2} 
            rx={width / 2 - 2} 
            ry={height / 2 - 2}
            fill={fillColor}
            stroke={strokeColor}
            strokeWidth="3"
          />
        </svg>
      );
    case 'oval':
      return (
        <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} style={svgStyle}>
          <ellipse 
            cx={width / 2} 
            cy={height / 2} 
            rx={width / 2 - 2} 
            ry={height / 2 - 2}
            fill={fillColor}
            stroke={strokeColor}
            strokeWidth="3"
          />
        </svg>
      );
    case 'square':
    case 'rectangle':
      return (
        <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} style={svgStyle}>
          <rect 
            x="2" y="2" 
            width={width - 4} height={height - 4}
            fill={fillColor}
            stroke={strokeColor}
            strokeWidth="3"
            rx="8"
          />
        </svg>
      );
    case 'lshape':
      return (
        <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} style={svgStyle}>
          <path 
            d={`M 0 0 L ${width * 0.25} 0 L ${width * 0.25} ${height * 0.75} L ${width} ${height * 0.75} L ${width} ${height} L 0 ${height} Z`}
            fill={fillColor}
            stroke={strokeColor}
            strokeWidth="2"
          />
        </svg>
      );
    case 'ushape':
      return (
        <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} style={svgStyle}>
          <path 
            d={`M 0 0 L ${width * 0.25} 0 L ${width * 0.25} ${height * 0.6} L ${width * 0.75} ${height * 0.6} L ${width * 0.75} 0 L ${width} 0 L ${width} ${height} L 0 ${height} Z`}
            fill={fillColor}
            stroke={strokeColor}
            strokeWidth="2"
          />
        </svg>
      );
    case 'sshape':
      // Serpentine 2-arc table - EXACT same SVG paths as canvas
      return (
        <svg width={width} height={height} viewBox="380 290 510 290" style={svgStyle} preserveAspectRatio="none">
          <path 
            d="M530.7,335.9c8.3.4,16.7,1.1,24.9,2.4,7.2,1.4,14.3,3.1,21.4,4.8,8.7,2.8,17.3,5.2,25.6,9.2,7.5,3.4,15.2,6.8,22.2,11.1,4.5,2.8,9.2,5.4,13.3,8.8,2,1.7,4.3,3,6.4,4.6,3.7,3.1,8,5.9,11.4,9.3,2.5,2.4,5.1,4.7,7.6,7-4.5,4.9-9.5,9.3-14,14.2-15.7,15.3-31.2,31-46.9,46.5-1.3,1.4-2.5,2.8-4,4-1.8-1.4-3.3-3.1-5-4.6-5.1-4-10.3-7.8-15.9-11.1-11-6-23.2-11.2-35.7-13-7.4-1.6-14.8-2.1-22.3-1.9-8.6-.1-16.6,1.7-25,3.2-4.5,1.3-9,2.6-13.4,4.4-5.2,1.7-9.7,4.4-14.6,6.8-8.2,4.3-15.1,10-22,16.1-12.9-12.6-25.3-25.5-38.2-38-8.4-8.3-16.6-16.7-25-24.8-.7-.7-1.1-1.3-1.5-2.2,11.1-10.2,23.1-20.2,36.1-27.8,6.3-4.2,13-7.4,19.7-10.6,8.2-3.6,16.2-7.2,25-9.4,2.3-.7,4.4-1.6,6.8-2.1,4.9-1,9.6-2.6,14.6-3.4,6.6-1.4,13.2-2.5,20-3,9.4-1,19-.6,28.5-.6h0Z"
            fill={fillColor}
            stroke={strokeColor}
            strokeWidth="2.8"
            strokeMiterlimit="10"
          />
          <path 
            d="M663.4,393.3c6.9,6.1,13.8,11.8,22,16.1,4.9,2.4,9.4,5.1,14.6,6.8,4.4,1.8,8.9,3.1,13.4,4.4,8.4,1.5,16.4,3.3,25,3.2,7.5.2,14.9-.3,22.3-1.9,12.5-1.8,24.7-7,35.7-13,5.6-3.3,10.8-7.1,15.9-11.1,1.7-1.5,3.2-3.2,5-4.6 L882.2,457.9c-2.5,2.3-5.1,4.6-7.6,7-3.4,3.4-7.7,6.2-11.4,9.3-2.1,1.6-4.4,2.9-6.4,4.6-4.1,3.4-8.8,6-13.3,8.8-7,4.3-14.7,7.7-22.2,11.1-8.3,4-16.9,6.4-25.6,9.2-7.1,1.7-14.2,3.4-21.4,4.8-8.2,1.3-16.6,2-24.9,2.4h0c-9.5.1-19.1.5-28.5-.5-6.8-.5-13.4-1.6-20-3-5-.8-9.7-2.4-14.6-3.4-2.4-.5-4.5-1.4-6.8-2.1-8.8-2.2-16.8-5.8-25-9.4-6.7-3.2-13.4-6.4-19.7-10.6-13-7.6-25-17.6-36.1-27.8 Z"
            fill={fillColor}
            stroke={strokeColor}
            strokeWidth="2.8"
            strokeMiterlimit="10"
          />
        </svg>
      );
    case 'crescent':
      return (
        <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} style={svgStyle}>
          <path 
            d={`M ${width * 0.1} ${height * 0.8} Q ${width * 0.1} ${height * 0.1} ${width * 0.5} ${height * 0.1} Q ${width * 0.9} ${height * 0.1} ${width * 0.9} ${height * 0.8} Q ${width * 0.7} ${height * 0.5} ${width * 0.5} ${height * 0.5} Q ${width * 0.3} ${height * 0.5} ${width * 0.1} ${height * 0.8} Z`}
            fill={fillColor}
            stroke={strokeColor}
            strokeWidth="2"
          />
        </svg>
      );
    case 'serpentine3':
      return (
        <svg width={width} height={height} viewBox="70 260 710 290" style={svgStyle} preserveAspectRatio="none">
          <path d="M215.1,336.1c8.3.4,16.7,1.1,24.9,2.4,7.2,1.4,14.3,3.1,21.4,4.8,8.7,2.8,17.3,5.2,25.6,9.2,7.5,3.4,15.2,6.8,22.2,11.1,4.5,2.8,9.2,5.4,13.3,8.8,2,1.7,4.3,3,6.4,4.6,3.7,3.1,8,5.9,11.4,9.3,2.5,2.4,5.1,4.7,7.6,7-4.5,4.9-9.5,9.3-14,14.2-15.7,15.3-31.2,31-46.9,46.5-1.3,1.4-2.5,2.8-4,4-1.8-1.4-3.3-3.1-5-4.6-5.1-4-10.3-7.8-15.9-11.1-11-6-23.2-11.2-35.7-13-7.4-1.6-14.8-2.1-22.3-1.9-8.6-.1-16.6,1.7-25,3.2-4.5,1.3-9,2.6-13.4,4.4-5.2,1.7-9.7,4.4-14.6,6.8-8.2,4.3-15.1,10-22,16.1-12.9-12.6-25.3-25.5-38.2-38-8.4-8.3-16.6-16.7-25-24.8-.7-.7-1.1-1.3-1.5-2.2,11.1-10.2,23.1-20.2,36.1-27.8,6.3-4.2,13-7.4,19.7-10.6,8.2-3.6,16.2-7.2,25-9.4,2.3-.7,4.4-1.6,6.8-2.1,4.9-1,9.6-2.6,14.6-3.4,6.6-1.4,13.2-2.5,20-3,9.4-1,19-.6,28.5-.6h0Z" fill={fillColor} stroke={strokeColor} strokeWidth="2.8" strokeMiterlimit="10"/>
          <path d="M347.8,393.5c6.9,6.1,13.8,11.8,22,16.1c4.9,2.4,9.4,5.1,14.6,6.8c4.4,1.8,8.9,3.1,13.4,4.4c8.4,1.5,16.4,3.3,25,3.2c7.5.2,14.9-.3,22.3-1.9c12.5-1.8,24.7-7,35.7-13c5.6-3.3,10.8-7.1,15.9-11.1c1.7-1.5,3.2-3.2,5-4.6L566.6,458.1c-2.5,2.3-5.1,4.6-7.6,7c-3.4,3.4-7.7,6.2-11.4,9.3c-2.1,1.6-4.4,2.9-6.4,4.6c-4.1,3.4-8.8,6-13.3,8.8c-7,4.3-14.7,7.7-22.2,11.1c-8.3,4-16.9,6.4-25.6,9.2c-7.1,1.7-14.2,3.4-21.4,4.8c-8.2,1.3-16.6,2-24.9,2.4h0c-9.5.1-19.1.5-28.5-.5c-6.8-.5-13.4-1.6-20-3c-5-.8-9.7-2.4-14.6-3.4c-2.4-.5-4.5-1.4-6.8-2.1c-8.8-2.2-16.8-5.8-25-9.4c-6.7-3.2-13.4-6.4-19.7-10.6c-13-7.6-25-17.6-36.1-27.8Z" fill={fillColor} stroke={strokeColor} strokeWidth="2.8" strokeMiterlimit="10"/>
          <path d="M653,336c8.3.4,16.7,1.1,24.9,2.4,7.2,1.4,14.3,3.1,21.4,4.8,8.7,2.8,17.3,5.2,25.6,9.2,7.5,3.4,15.2,6.8,22.2,11.1,4.5,2.8,9.2,5.4,13.3,8.8,2,1.7,4.3,3,6.4,4.6,3.7,3.1,8,5.9,11.4,9.3,2.5,2.4,5.1,4.7,7.6,7-4.5,4.9-9.5,9.3-14,14.2-15.7,15.3-31.2,31-46.9,46.5-1.3,1.4-2.5,2.8-4,4-1.8-1.4-3.3-3.1-5-4.6-5.1-4-10.3-7.8-15.9-11.1-11-6-23.2-11.2-35.7-13-7.4-1.6-14.8-2.1-22.3-1.9-8.6,0-16.6,1.7-25,3.2-4.5,1.3-9,2.6-13.4,4.4-5.2,1.7-9.7,4.4-14.6,6.8-8.2,4.3-15.1,10-22,16.1-12.9-12.6-25.3-25.5-38.2-38-8.4-8.3-16.6-16.7-25-24.8-.7-.7-1.1-1.3-1.5-2.2,11.1-10.2,23.1-20.2,36.1-27.8,6.3-4.2,13-7.4,19.7-10.6,8.2-3.6,16.2-7.2,25-9.4,2.3-.7,4.4-1.6,6.8-2.1,4.9-1,9.6-2.6,14.6-3.4,6.6-1.4,13.2-2.5,20-3,9.4-1,19-.6,28.5-.6h0Z" fill={fillColor} stroke={strokeColor} strokeWidth="2.8" strokeMiterlimit="10"/>
        </svg>
      );
    case 'serpentine4':
      return (
        <svg width={width} height={height} viewBox="70 260 925 290" style={svgStyle} preserveAspectRatio="none">
          <path d="M215.1,336.1c8.3.4,16.7,1.1,24.9,2.4,7.2,1.4,14.3,3.1,21.4,4.8,8.7,2.8,17.3,5.2,25.6,9.2,7.5,3.4,15.2,6.8,22.2,11.1,4.5,2.8,9.2,5.4,13.3,8.8,2,1.7,4.3,3,6.4,4.6,3.7,3.1,8,5.9,11.4,9.3,2.5,2.4,5.1,4.7,7.6,7-4.5,4.9-9.5,9.3-14,14.2-15.7,15.3-31.2,31-46.9,46.5-1.3,1.4-2.5,2.8-4,4-1.8-1.4-3.3-3.1-5-4.6-5.1-4-10.3-7.8-15.9-11.1-11-6-23.2-11.2-35.7-13-7.4-1.6-14.8-2.1-22.3-1.9-8.6-.1-16.6,1.7-25,3.2-4.5,1.3-9,2.6-13.4,4.4-5.2,1.7-9.7,4.4-14.6,6.8-8.2,4.3-15.1,10-22,16.1-12.9-12.6-25.3-25.5-38.2-38-8.4-8.3-16.6-16.7-25-24.8-.7-.7-1.1-1.3-1.5-2.2,11.1-10.2,23.1-20.2,36.1-27.8,6.3-4.2,13-7.4,19.7-10.6,8.2-3.6,16.2-7.2,25-9.4,2.3-.7,4.4-1.6,6.8-2.1,4.9-1,9.6-2.6,14.6-3.4,6.6-1.4,13.2-2.5,20-3,9.4-1,19-.6,28.5-.6h0Z" fill={fillColor} stroke={strokeColor} strokeWidth="2.8" strokeMiterlimit="10"/>
          <path d="M347.8,393.5c6.9,6.1,13.8,11.8,22,16.1c4.9,2.4,9.4,5.1,14.6,6.8c4.4,1.8,8.9,3.1,13.4,4.4c8.4,1.5,16.4,3.3,25,3.2c7.5.2,14.9-.3,22.3-1.9c12.5-1.8,24.7-7,35.7-13c5.6-3.3,10.8-7.1,15.9-11.1c1.7-1.5,3.2-3.2,5-4.6L566.6,458.1c-2.5,2.3-5.1,4.6-7.6,7c-3.4,3.4-7.7,6.2-11.4,9.3c-2.1,1.6-4.4,2.9-6.4,4.6c-4.1,3.4-8.8,6-13.3,8.8c-7,4.3-14.7,7.7-22.2,11.1c-8.3,4-16.9,6.4-25.6,9.2c-7.1,1.7-14.2,3.4-21.4,4.8c-8.2,1.3-16.6,2-24.9,2.4h0c-9.5.1-19.1.5-28.5-.5c-6.8-.5-13.4-1.6-20-3c-5-.8-9.7-2.4-14.6-3.4c-2.4-.5-4.5-1.4-6.8-2.1c-8.8-2.2-16.8-5.8-25-9.4c-6.7-3.2-13.4-6.4-19.7-10.6c-13-7.6-25-17.6-36.1-27.8Z" fill={fillColor} stroke={strokeColor} strokeWidth="2.8" strokeMiterlimit="10"/>
          <path d="M653,336c8.3.4,16.7,1.1,24.9,2.4,7.2,1.4,14.3,3.1,21.4,4.8,8.7,2.8,17.3,5.2,25.6,9.2,7.5,3.4,15.2,6.8,22.2,11.1,4.5,2.8,9.2,5.4,13.3,8.8,2,1.7,4.3,3,6.4,4.6,3.7,3.1,8,5.9,11.4,9.3,2.5,2.4,5.1,4.7,7.6,7-4.5,4.9-9.5,9.3-14,14.2-15.7,15.3-31.2,31-46.9,46.5-1.3,1.4-2.5,2.8-4,4-1.8-1.4-3.3-3.1-5-4.6-5.1-4-10.3-7.8-15.9-11.1-11-6-23.2-11.2-35.7-13-7.4-1.6-14.8-2.1-22.3-1.9-8.6,0-16.6,1.7-25,3.2-4.5,1.3-9,2.6-13.4,4.4-5.2,1.7-9.7,4.4-14.6,6.8-8.2,4.3-15.1,10-22,16.1-12.9-12.6-25.3-25.5-38.2-38-8.4-8.3-16.6-16.7-25-24.8-.7-.7-1.1-1.3-1.5-2.2,11.1-10.2,23.1-20.2,36.1-27.8,6.3-4.2,13-7.4,19.7-10.6,8.2-3.6,16.2-7.2,25-9.4,2.3-.7,4.4-1.6,6.8-2.1,4.9-1,9.6-2.6,14.6-3.4,6.6-1.4,13.2-2.5,20-3,9.4-1,19-.6,28.5-.6h0Z" fill={fillColor} stroke={strokeColor} strokeWidth="2.8" strokeMiterlimit="10"/>
          <path d="M785.8,393.5c6.9,6.1,13.8,11.8,22,16.1c4.9,2.4,9.4,5.1,14.6,6.8c4.4,1.8,8.9,3.1,13.4,4.4c8.4,1.5,16.4,3.3,25,3.2c7.5.2,14.9-.3,22.3-1.9c12.5-1.8,24.7-7,35.7-13c5.6-3.3,10.8-7.1,15.9-11.1c1.7-1.5,3.2-3.2,5-4.6L1004.6,458.1c-2.5,2.3-5.1,4.6-7.6,7c-3.4,3.4-7.7,6.2-11.4,9.3c-2.1,1.6-4.4,2.9-6.4,4.6c-4.1,3.4-8.8,6-13.3,8.8c-7,4.3-14.7,7.7-22.2,11.1c-8.3,4-16.9,6.4-25.6,9.2c-7.1,1.7-14.2,3.4-21.4,4.8c-8.2,1.3-16.6,2-24.9,2.4h0c-9.5.1-19.1.5-28.5-.5c-6.8-.5-13.4-1.6-20-3c-5-.8-9.7-2.4-14.6-3.4c-2.4-.5-4.5-1.4-6.8-2.1c-8.8-2.2-16.8-5.8-25-9.4c-6.7-3.2-13.4-6.4-19.7-10.6c-13-7.6-25-17.6-36.1-27.8Z" fill={fillColor} stroke={strokeColor} strokeWidth="2.8" strokeMiterlimit="10"/>
        </svg>
      );
    case 'serpentine5':
      return (
        <svg width={width} height={height} viewBox="70 260 1145 290" style={svgStyle} preserveAspectRatio="none">
          <path d="M215.1,336.1c8.3.4,16.7,1.1,24.9,2.4,7.2,1.4,14.3,3.1,21.4,4.8,8.7,2.8,17.3,5.2,25.6,9.2,7.5,3.4,15.2,6.8,22.2,11.1,4.5,2.8,9.2,5.4,13.3,8.8,2,1.7,4.3,3,6.4,4.6,3.7,3.1,8,5.9,11.4,9.3,2.5,2.4,5.1,4.7,7.6,7-4.5,4.9-9.5,9.3-14,14.2-15.7,15.3-31.2,31-46.9,46.5-1.3,1.4-2.5,2.8-4,4-1.8-1.4-3.3-3.1-5-4.6-5.1-4-10.3-7.8-15.9-11.1-11-6-23.2-11.2-35.7-13-7.4-1.6-14.8-2.1-22.3-1.9-8.6-.1-16.6,1.7-25,3.2-4.5,1.3-9,2.6-13.4,4.4-5.2,1.7-9.7,4.4-14.6,6.8-8.2,4.3-15.1,10-22,16.1-12.9-12.6-25.3-25.5-38.2-38-8.4-8.3-16.6-16.7-25-24.8-.7-.7-1.1-1.3-1.5-2.2,11.1-10.2,23.1-20.2,36.1-27.8,6.3-4.2,13-7.4,19.7-10.6,8.2-3.6,16.2-7.2,25-9.4,2.3-.7,4.4-1.6,6.8-2.1,4.9-1,9.6-2.6,14.6-3.4,6.6-1.4,13.2-2.5,20-3,9.4-1,19-.6,28.5-.6h0Z" fill={fillColor} stroke={strokeColor} strokeWidth="2.8" strokeMiterlimit="10"/>
          <path d="M347.8,393.5c6.9,6.1,13.8,11.8,22,16.1c4.9,2.4,9.4,5.1,14.6,6.8c4.4,1.8,8.9,3.1,13.4,4.4c8.4,1.5,16.4,3.3,25,3.2c7.5.2,14.9-.3,22.3-1.9c12.5-1.8,24.7-7,35.7-13c5.6-3.3,10.8-7.1,15.9-11.1c1.7-1.5,3.2-3.2,5-4.6L566.6,458.1c-2.5,2.3-5.1,4.6-7.6,7c-3.4,3.4-7.7,6.2-11.4,9.3c-2.1,1.6-4.4,2.9-6.4,4.6c-4.1,3.4-8.8,6-13.3,8.8c-7,4.3-14.7,7.7-22.2,11.1c-8.3,4-16.9,6.4-25.6,9.2c-7.1,1.7-14.2,3.4-21.4,4.8c-8.2,1.3-16.6,2-24.9,2.4h0c-9.5.1-19.1.5-28.5-.5c-6.8-.5-13.4-1.6-20-3c-5-.8-9.7-2.4-14.6-3.4c-2.4-.5-4.5-1.4-6.8-2.1c-8.8-2.2-16.8-5.8-25-9.4c-6.7-3.2-13.4-6.4-19.7-10.6c-13-7.6-25-17.6-36.1-27.8Z" fill={fillColor} stroke={strokeColor} strokeWidth="2.8" strokeMiterlimit="10"/>
          <path d="M653,336c8.3.4,16.7,1.1,24.9,2.4,7.2,1.4,14.3,3.1,21.4,4.8,8.7,2.8,17.3,5.2,25.6,9.2,7.5,3.4,15.2,6.8,22.2,11.1,4.5,2.8,9.2,5.4,13.3,8.8,2,1.7,4.3,3,6.4,4.6,3.7,3.1,8,5.9,11.4,9.3,2.5,2.4,5.1,4.7,7.6,7-4.5,4.9-9.5,9.3-14,14.2-15.7,15.3-31.2,31-46.9,46.5-1.3,1.4-2.5,2.8-4,4-1.8-1.4-3.3-3.1-5-4.6-5.1-4-10.3-7.8-15.9-11.1-11-6-23.2-11.2-35.7-13-7.4-1.6-14.8-2.1-22.3-1.9-8.6,0-16.6,1.7-25,3.2-4.5,1.3-9,2.6-13.4,4.4-5.2,1.7-9.7,4.4-14.6,6.8-8.2,4.3-15.1,10-22,16.1-12.9-12.6-25.3-25.5-38.2-38-8.4-8.3-16.6-16.7-25-24.8-.7-.7-1.1-1.3-1.5-2.2,11.1-10.2,23.1-20.2,36.1-27.8,6.3-4.2,13-7.4,19.7-10.6,8.2-3.6,16.2-7.2,25-9.4,2.3-.7,4.4-1.6,6.8-2.1,4.9-1,9.6-2.6,14.6-3.4,6.6-1.4,13.2-2.5,20-3,9.4-1,19-.6,28.5-.6h0Z" fill={fillColor} stroke={strokeColor} strokeWidth="2.8" strokeMiterlimit="10"/>
          <path d="M785.8,393.5c6.9,6.1,13.8,11.8,22,16.1c4.9,2.4,9.4,5.1,14.6,6.8c4.4,1.8,8.9,3.1,13.4,4.4c8.4,1.5,16.4,3.3,25,3.2c7.5.2,14.9-.3,22.3-1.9c12.5-1.8,24.7-7,35.7-13c5.6-3.3,10.8-7.1,15.9-11.1c1.7-1.5,3.2-3.2,5-4.6L1004.6,458.1c-2.5,2.3-5.1,4.6-7.6,7c-3.4,3.4-7.7,6.2-11.4,9.3c-2.1,1.6-4.4,2.9-6.4,4.6c-4.1,3.4-8.8,6-13.3,8.8c-7,4.3-14.7,7.7-22.2,11.1c-8.3,4-16.9,6.4-25.6,9.2c-7.1,1.7-14.2,3.4-21.4,4.8c-8.2,1.3-16.6,2-24.9,2.4h0c-9.5.1-19.1.5-28.5-.5c-6.8-.5-13.4-1.6-20-3c-5-.8-9.7-2.4-14.6-3.4c-2.4-.5-4.5-1.4-6.8-2.1c-8.8-2.2-16.8-5.8-25-9.4c-6.7-3.2-13.4-6.4-19.7-10.6c-13-7.6-25-17.6-36.1-27.8Z" fill={fillColor} stroke={strokeColor} strokeWidth="2.8" strokeMiterlimit="10"/>
          <path d="M1091,336c8.3.4,16.7,1.1,24.9,2.4,7.2,1.4,14.3,3.1,21.4,4.8,8.7,2.8,17.3,5.2,25.6,9.2,7.5,3.4,15.2,6.8,22.2,11.1,4.5,2.8,9.2,5.4,13.3,8.8,2,1.7,4.3,3,6.4,4.6,3.7,3.1,8,5.9,11.4,9.3,2.5,2.4,5.1,4.7,7.6,7-4.5,4.9-9.5,9.3-14,14.2-15.7,15.3-31.2,31-46.9,46.5-1.3,1.4-2.5,2.8-4,4-1.8-1.4-3.3-3.1-5-4.6-5.1-4-10.3-7.8-15.9-11.1-11-6-23.2-11.2-35.7-13-7.4-1.6-14.8-2.1-22.3-1.9-8.6,0-16.6,1.7-25,3.2-4.5,1.3-9,2.6-13.4,4.4-5.2,1.7-9.7,4.4-14.6,6.8-8.2,4.3-15.1,10-22,16.1-12.9-12.6-25.3-25.5-38.2-38-8.4-8.3-16.6-16.7-25-24.8-.7-.7-1.1-1.3-1.5-2.2,11.1-10.2,23.1-20.2,36.1-27.8,6.3-4.2,13-7.4,19.7-10.6,8.2-3.6,16.2-7.2,25-9.4,2.3-.7,4.4-1.6,6.8-2.1,4.9-1,9.6-2.6,14.6-3.4,6.6-1.4,13.2-2.5,20-3,9.4-1,19-.6,28.5-.6h0Z" fill={fillColor} stroke={strokeColor} strokeWidth="2.8" strokeMiterlimit="10"/>
        </svg>
      );
    case 'serpentine6':
    case 'serpentine7':
      // For serpentine 6-7, use similar pattern - simplified for brevity
      const numArcs = shape === 'serpentine6' ? 6 : 7;
      const viewBoxWidth = 70 + numArcs * 220;
      return (
        <svg width={width} height={height} viewBox={`70 260 ${viewBoxWidth - 70} 290`} style={svgStyle} preserveAspectRatio="none">
          {Array.from({ length: numArcs }, (_, i) => {
            const xOffset = i * 219;
            const isUp = i % 2 === 0;
            return isUp ? (
              <path key={i} d={`M${215.1 + xOffset},336.1c8.3.4,16.7,1.1,24.9,2.4,7.2,1.4,14.3,3.1,21.4,4.8,8.7,2.8,17.3,5.2,25.6,9.2,7.5,3.4,15.2,6.8,22.2,11.1,4.5,2.8,9.2,5.4,13.3,8.8,2,1.7,4.3,3,6.4,4.6,3.7,3.1,8,5.9,11.4,9.3,2.5,2.4,5.1,4.7,7.6,7-4.5,4.9-9.5,9.3-14,14.2-15.7,15.3-31.2,31-46.9,46.5-1.3,1.4-2.5,2.8-4,4-1.8-1.4-3.3-3.1-5-4.6-5.1-4-10.3-7.8-15.9-11.1-11-6-23.2-11.2-35.7-13-7.4-1.6-14.8-2.1-22.3-1.9-8.6-.1-16.6,1.7-25,3.2-4.5,1.3-9,2.6-13.4,4.4-5.2,1.7-9.7,4.4-14.6,6.8-8.2,4.3-15.1,10-22,16.1-12.9-12.6-25.3-25.5-38.2-38-8.4-8.3-16.6-16.7-25-24.8-.7-.7-1.1-1.3-1.5-2.2,11.1-10.2,23.1-20.2,36.1-27.8,6.3-4.2,13-7.4,19.7-10.6,8.2-3.6,16.2-7.2,25-9.4,2.3-.7,4.4-1.6,6.8-2.1,4.9-1,9.6-2.6,14.6-3.4,6.6-1.4,13.2-2.5,20-3,9.4-1,19-.6,28.5-.6h0Z`} fill={fillColor} stroke={strokeColor} strokeWidth="2.8" strokeMiterlimit="10"/>
            ) : (
              <path key={i} d={`M${347.8 + xOffset - 219},393.5c6.9,6.1,13.8,11.8,22,16.1c4.9,2.4,9.4,5.1,14.6,6.8c4.4,1.8,8.9,3.1,13.4,4.4c8.4,1.5,16.4,3.3,25,3.2c7.5.2,14.9-.3,22.3-1.9c12.5-1.8,24.7-7,35.7-13c5.6-3.3,10.8-7.1,15.9-11.1c1.7-1.5,3.2-3.2,5-4.6L${566.6 + xOffset - 219},458.1c-2.5,2.3-5.1,4.6-7.6,7c-3.4,3.4-7.7,6.2-11.4,9.3c-2.1,1.6-4.4,2.9-6.4,4.6c-4.1,3.4-8.8,6-13.3,8.8c-7,4.3-14.7,7.7-22.2,11.1c-8.3,4-16.9,6.4-25.6,9.2c-7.1,1.7-14.2,3.4-21.4,4.8c-8.2,1.3-16.6,2-24.9,2.4h0c-9.5.1-19.1.5-28.5-.5c-6.8-.5-13.4-1.6-20-3c-5-.8-9.7-2.4-14.6-3.4c-2.4-.5-4.5-1.4-6.8-2.1c-8.8-2.2-16.8-5.8-25-9.4c-6.7-3.2-13.4-6.4-19.7-10.6c-13-7.6-25-17.6-36.1-27.8Z`} fill={fillColor} stroke={strokeColor} strokeWidth="2.8" strokeMiterlimit="10"/>
            );
          })}
        </svg>
      );
    case 'halfround':
      return (
        <svg width={width} height={height} viewBox="200 310 450 220" style={svgStyle} preserveAspectRatio="xMidYMid meet">
          <path 
            d="M278.2,401.9c6.8-6.7,13.3-13.7,20.7-19.6,5.2-4.4,10.7-8.3,16.4-11.9,4.1-2.9,8.6-5.1,12.8-7.8,2.1-1.3,4.2-2.2,6.3-3.3,7.8-4.6,16-7.7,24.4-10.8,7-2.4,14-4.8,21.5-6.3,14.6-3.7,30.2-5,45.2-5.5.3.9.5,1.6.5,2.6-.3,11.7-.3,23.4-.4,35.2-.4,18-.2,35.8-.6,53.9-9.2.5-18.1,1.3-27,4-5.2,1.7-10.3,3-15.2,5.4-4.4,1.8-8.5,4-12.6,6.3-7,4.8-14,9.2-20.1,15.3-5.5,5.1-10.4,10.7-14.6,17-7.6,10.1-12.7,22.3-16.3,34.3-1.7,6.3-2.7,12.6-3.5,19.1-.2,2.3,0,4.5-.3,6.8-1.9.2-3.7,0-5.7,0-22.1,0-44.1-.3-66-.2-6.6-.3-13.3,0-19.9-.3.2-3.4.4-6.9.5-10.3,0-4.8,1.1-9.8,1.6-14.6.4-2.6,1.1-5.2,1.3-7.8.5-5.3,2.1-10.4,3.3-15.6,2-8,5.1-15.8,8-23.5,3.1-8.7,7.6-16.4,11.8-24.5,3.9-6.2,7.7-12.4,11.9-18.4,4.9-6.7,10.4-13.1,16.1-19.2h0Z"
            fill={fillColor}
            stroke={strokeColor}
            strokeWidth="2.8"
            strokeMiterlimit="10"
          />
          <path 
            d="M426.6,336.8c15.1.7,30.6,2.2,45.1,6.1,7.4,1.5,14.4,4,21.4,6.6,8.3,3.3,16.5,6.5,24.3,11.2,2.1,1.1,4.2,2,6.3,3.4,4.2,2.8,8.6,5,12.7,8,5.6,3.7,11.1,7.6,16.2,12.1,7.3,6,13.8,13.1,20.5,19.9h0c5.5,6.3,11,12.7,15.8,19.5,4.1,6.1,7.8,12.4,11.6,18.6,4.1,8.2,8.5,16,11.4,24.7,2.9,7.7,5.8,15.6,7.7,23.6,1.2,5.2,2.6,10.3,3.1,15.6.2,2.6.9,5.2,1.2,7.8.4,4.8,1.4,9.8,1.4,14.6,0,3.5.2,6.9.4,10.3-6.6.2-13.3-.2-19.9,0-21.9-.4-44-.4-66-.7-1.9,0-3.7.2-5.7,0-.3-2.3-.1-4.5-.2-6.8-.7-6.4-1.7-12.8-3.3-19.1-3.5-12-8.3-24.4-15.8-34.5-4.1-6.4-8.9-12-14.3-17.2-6-6.2-12.9-10.6-19.8-15.5-4.1-2.3-8.2-4.6-12.5-6.4-4.9-2.5-9.9-3.8-15.1-5.6-8.8-2.8-17.7-3.7-26.9-4.3"
            fill={fillColor}
            stroke={strokeColor}
            strokeWidth="2.8"
            strokeMiterlimit="10"
          />
        </svg>
      );
    default:
      return (
        <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} style={svgStyle}>
          <rect x="2" y="2" width={width - 4} height={height - 4} fill={fillColor} stroke={strokeColor} strokeWidth="3" rx="8"/>
        </svg>
      );
  }
}

// Render venue element preview
function renderVenueElementPreview(elementType, width, height) {
  const config = VENUE_ELEMENT_CONFIGS[elementType];
  const isCircle = config?.isCircle;
  
  return (
    <div style={{ width: '100%', height: '100%', borderRadius: isCircle ? '50%' : '4px', backgroundColor: 'rgba(255, 255, 255, 0.9)', border: '3px dashed #64748b', display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative' }}>
      <span style={{ position: 'absolute', bottom: -20, left: '50%', transform: 'translateX(-50%)', fontSize: '11px', color: '#fff', backgroundColor: '#64748b', padding: '2px 8px', borderRadius: '4px', fontWeight: 500, whiteSpace: 'nowrap' }}>
        {config?.label || elementType}
      </span>
    </div>
  );
}

// Render custom piece preview
function renderCustomPiecePreview(pieceType, width, height) {
  const config = CUSTOM_PIECE_CONFIGS[pieceType];
  const isCircle = config?.isCircle;
  const isArc = config?.isArc;
  
  if (isArc && config?.svgPath) {
    // Arc shape preview using original SVG
    return (
      <div style={{ width: '100%', height: '100%', position: 'relative' }}>
        <svg width={width} height={height} viewBox={config.viewBox} style={{ position: 'absolute', inset: 0 }} preserveAspectRatio="none">
          <path d={config.svgPath} fill="rgba(59, 130, 246, 0.8)" stroke="#1e40af" strokeWidth="3"/>
        </svg>
        <span style={{ position: 'absolute', bottom: -20, left: '50%', transform: 'translateX(-50%)', fontSize: '11px', color: '#fff', backgroundColor: '#3B82F6', padding: '2px 8px', borderRadius: '4px', fontWeight: 500, whiteSpace: 'nowrap' }}>
          {config?.label || pieceType}
        </span>
      </div>
    );
  }
  
  return (
    <div style={{ width: '100%', height: '100%', borderRadius: isCircle ? '50%' : '4px', backgroundColor: 'rgba(59, 130, 246, 0.8)', border: '2px solid #1e40af', display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative' }}>
      <span style={{ position: 'absolute', bottom: -20, left: '50%', transform: 'translateX(-50%)', fontSize: '11px', color: '#fff', backgroundColor: '#3B82F6', padding: '2px 8px', borderRadius: '4px', fontWeight: 500, whiteSpace: 'nowrap' }}>
        {config?.label || pieceType}
      </span>
    </div>
  );
}

export default function CustomDragLayer({ zoom = 1 }) {
  const { isDragging, item, itemType, currentSourceOffset, currentOffset } = useDragLayer((monitor) => ({
    item: monitor.getItem(),
    itemType: monitor.getItemType(),
    currentSourceOffset: monitor.getSourceClientOffset(),
    currentOffset: monitor.getClientOffset(),
    isDragging: monitor.isDragging(),
  }));

  if (!isDragging || !item) return null;
  
  const isExistingPiece = itemType === 'CUSTOM_PIECE' || itemType === 'VENUE_ELEMENT' || itemType === 'TABLE';
  const displayOffset = isExistingPiece && currentSourceOffset ? currentSourceOffset : currentOffset;
  
  if (!displayOffset) return null;

  // Handle venue elements
  if (itemType === 'VENUE_ELEMENT' || itemType === 'VENUE_ELEMENT_NEW') {
    const elementType = item.element_type;
    const config = VENUE_ELEMENT_CONFIGS[elementType] || {};
    const width = (item.width || config.width || 100) * zoom;
    const height = (item.height || config.height || 60) * zoom;
    const rotation = item.rotation || 0;
    const left = isExistingPiece && currentSourceOffset ? displayOffset.x : displayOffset.x - width / 2;
    const top = isExistingPiece && currentSourceOffset ? displayOffset.y : displayOffset.y - height / 2;
    
    return (
      <div style={{ position: 'fixed', pointerEvents: 'none', zIndex: 9999, left, top, width, height, transform: rotation ? `rotate(${rotation}deg)` : undefined, boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)' }}>
        {renderVenueElementPreview(elementType, width, height)}
      </div>
    );
  }

  // Handle custom pieces
  if (itemType === 'CUSTOM_PIECE' || itemType === 'CUSTOM_PIECE_NEW') {
    const pieceType = item.piece_type;
    const config = CUSTOM_PIECE_CONFIGS[pieceType] || {};
    const width = (item.width || config.width || 60) * zoom;
    const height = (item.height || config.height || 60) * zoom;
    const rotation = item.rotation || 0;
    const isArc = config.isArc;
    const shouldCenterOnCursor = isArc || !isExistingPiece || !currentSourceOffset;
    const left = shouldCenterOnCursor ? currentOffset.x - width / 2 : displayOffset.x;
    const top = shouldCenterOnCursor ? currentOffset.y - height / 2 : displayOffset.y;
    
    return (
      <div style={{ position: 'fixed', pointerEvents: 'none', zIndex: 9999, left, top, width, height, transform: rotation ? `rotate(${rotation}deg)` : undefined, boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)' }}>
        {renderCustomPiecePreview(pieceType, width, height)}
      </div>
    );
  }

  // Handle tables - render exact SVG shape (no seats)
  const shape = item.shape || 'round';
  const config = SHAPE_CONFIGS[shape] || SHAPE_CONFIGS.round;
  const baseWidth = item.width || config.width;
  const baseHeight = item.height || config.height;
  const width = baseWidth * zoom;
  const height = baseHeight * zoom;
  const rotation = item.rotation || 0;
  
  // For rotated tables, we need to swap dimensions for the preview container
  const isVerticallyRotated = rotation === 90 || rotation === 270;
  
  // The preview container size should match the VISUAL footprint
  const previewWidth = isVerticallyRotated ? height : width;
  const previewHeight = isVerticallyRotated ? width : height;
  
  // SIMPLE APPROACH: Always center preview on cursor for consistent behavior
  // This ensures the preview follows the mouse predictably regardless of rotation
  const left = currentOffset.x - previewWidth / 2;
  const top = currentOffset.y - previewHeight / 2;

  return (
    <div style={{ 
      position: 'fixed', 
      pointerEvents: 'none', 
      zIndex: 9999, 
      left, 
      top, 
      width: previewWidth, 
      height: previewHeight,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    }}>
      <div style={{ 
        width: width, 
        height: height, 
        position: 'relative', 
        boxShadow: '0 4px 16px rgba(0, 0, 0, 0.5)',
        transform: rotation ? `rotate(${rotation}deg)` : undefined,
        transformOrigin: 'center center'
      }}>
        {renderDragPreviewSVG(shape, width, height)}
        <span style={{ fontSize: '12px', fontWeight: 600, color: 'white', textAlign: 'center', maxWidth: width - 8, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', position: 'absolute', left: '50%', top: '50%', transform: 'translate(-50%, -50%)', zIndex: 10, textShadow: '0 1px 2px rgba(0,0,0,0.8)' }}>
          {item.table_name || config.label}
        </span>
      </div>
    </div>
  );
}
